import Typography from "@vapor/react-extended/ExtendedTypography";
import { Xmark } from "@vapor/react-icons";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    useTheme,
} from "@vapor/react-material";
import { DatePicker } from "@vapor/react-x-date-pickers";
import dayjs from "dayjs";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ADE_URL } from "../../../../../../config";
import { useSuccessNotification } from "../../../../../../core/hooks/useSuccessNotification";

interface EditAgencyIdModalProps {
    taxSubmissionId?: string;
    loading: boolean;
    open: boolean;
    onClose: () => void;
    onEdit: (
        taxSubmissionId: string,
        agencyId: string,
        sendDate: Date,
    ) => Promise<void>;
}

export const UpdateAgencyDataModal: React.FC<EditAgencyIdModalProps> = ({
    taxSubmissionId,
    loading,
    open,
    onClose,
    onEdit,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { showTextMessage: showSuccessNotification } =
        useSuccessNotification();

    const [agencyData, setAgencyData] = useState({
        id: "",
        sendDate: dayjs().toDate(),
    });
    const [agencyIdError, setAgencyIdError] = useState("");
    const [isAgencyIdVerified, setIsAgencyIdVerified] = useState(false);

    const handleOnClose = () => {
        setAgencyData({ id: "", sendDate: dayjs().toDate() });
        setAgencyIdError("");
        setIsAgencyIdVerified(false);
        onClose();
    };

    const handleOnUpdate = async () => {
        if (!taxSubmissionId) return;

        try {
            await onEdit(taxSubmissionId, agencyData.id, agencyData.sendDate);
        } catch {
            // error notification already shown by handleHttpError
            return;
        }
        showSuccessNotification(
            t(
                "views.home.components.tax_submission_detail_summary.update_agency_data_modal.success",
            ),
        );
        handleOnClose();
    };

    const handleAgencyIdOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setAgencyData((prev) => ({
            ...prev,
            id: value,
        }));
        setAgencyIdError(
            !value.match(/^\d{17}$/)
                ? t(
                      "views.home.components.tax_submission_detail_summary.update_agency_data_modal.input_error",
                  )
                : "",
        );
    };

    return (
        <Dialog open={open} onClose={handleOnClose}>
            <DialogTitle>
                <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        width: "100%",
                    }}
                >
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        variant="bodyLarge"
                    >
                        {t(
                            "views.home.components.tax_submission_detail_summary.update_agency_data_modal.title",
                        )}
                    </Typography>
                    <IconButton
                        disabled={loading}
                        onClick={handleOnClose}
                        sx={{ padding: 0 }}
                    >
                        <Xmark color={loading ? "disabled" : "interactive"} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider variant="fullWidth" />
            <DialogContent>
                <Stack justifyContent="space-between" gap={2}>
                    <Box sx={{ display: "inline" }}>
                        <Trans
                            i18nKey={t(
                                "views.home.components.tax_submission_detail_summary.update_agency_data_modal.description",
                            )}
                            components={{
                                1: (
                                    <a
                                        href={ADE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ),
                            }}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isAgencyIdVerified}
                                onChange={() => {
                                    setIsAgencyIdVerified(
                                        (prevValue) => !prevValue,
                                    );
                                }}
                            />
                        }
                        label={t(
                            "views.home.components.tax_submission_detail_summary.update_agency_data_modal.checkobox",
                        )}
                        sx={{ width: "fit-content" }}
                    />
                    <TextField
                        disabled={!isAgencyIdVerified}
                        label={t(
                            "views.home.components.tax_submission_detail_summary.update_agency_data_modal.agency_id_label",
                        )}
                        error={!!agencyIdError}
                        helperText={agencyIdError}
                        onChange={handleAgencyIdOnChange}
                        sx={{ width: 250 }}
                        value={agencyData.id}
                    />

                    <DatePicker
                        disabled={!isAgencyIdVerified}
                        label={t(
                            "views.home.components.tax_submission_detail_summary.update_agency_data_modal.agency_data_label",
                        )}
                        onChange={(date: unknown) => {
                            setAgencyData((prev) => ({
                                ...prev,
                                sendDate: date as Date,
                            }));
                        }}
                        sx={{ width: 250 }}
                        value={agencyData.sendDate}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={loading}
                    variant="outlined"
                    onClick={handleOnClose}
                >
                    {t(
                        "views.home.components.tax_submission_detail_summary.update_agency_data_modal.back",
                    )}
                </Button>
                <Button
                    variant="contained"
                    disabled={
                        !agencyData.id ||
                        !dayjs(agencyData.sendDate).isValid() ||
                        !!agencyIdError ||
                        !isAgencyIdVerified ||
                        loading ||
                        !taxSubmissionId
                    }
                    onClick={handleOnUpdate}
                >
                    {t(
                        "views.home.components.tax_submission_detail_summary.update_agency_data_modal.confirm",
                    )}
                    {loading && (
                        <CircularProgress
                            color="inherit"
                            sx={{ ml: 1 }}
                            size={16}
                        />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
